/**
 * Messages for one area of the app (document 15, section 5.1), English and Arabic side by side.
 * Every key lives under `mobileScreens`; Arabic plurals have all six CLDR forms.
 * The phone app (apps/mobile): every screen apart from the navigator and More (`mobile.*`).
 */
export const en = {
  mobileScreens: {
    kit: {
      tryAgain: 'Try again',
      comingSoon: 'Coming soon',
      unreadBadge: '{{label}}, {{count}} unread',
    },
    status: {
      void: 'Void',
      draft: 'Draft',
      applied: 'Applied',
      partlyApplied: 'Partly applied',
      unapplied: 'Unapplied',
      paid: 'Paid',
      overdue: 'Overdue',
      overduePartlyPaid: 'Overdue · partly paid',
      partiallyPaid: 'Partially paid',
      unpaid: 'Unpaid',
      recorded: 'Recorded',
      approved: 'Approved',
      withdrawn: 'Withdrawn',
      notRecorded: 'Not recorded',
      rejected: 'Rejected',
    },
    filter: {
      all: 'All',
      unpaid: 'Unpaid',
      overdue: 'Overdue',
      paid: 'Paid',
      drafts: 'Drafts',
    },
    roles: {
      admin: 'Admin',
      accountant: 'Accountant',
      owner: 'Owner',
      viewer: 'Viewer',
      rejectWhy: 'Say why you are rejecting it.',
      commentTooLong: 'Keep the comment under 2,000 characters.',
    },
    portal: {
      kindBill: 'Bill to pay',
      kindReceipt: 'Receipt',
      kindInvoice: 'Invoice',
      kindOther: 'Other',
      toReview: 'To review',
      recorded: 'Recorded',
      notRecorded: 'Not recorded',
      all: 'All',
      recordedExpense: 'Recorded as an expense for {{name}}',
      recordedInvoice: 'Recorded as an invoice for {{name}}',
      recordedBill: 'Recorded as a bill for {{name}}',
      awaitingApproval: 'Awaiting approval',
      approved: 'Approved',
      rejected: 'Rejected',
      withdrawn: 'Withdrawn',
      readyToPost: 'Approved by you, ready to post',
      waitingForYou: 'Waiting for you',
      withAccountant: 'With your accountant',
      fileEmpty: '{{name}} is empty.',
      fileTooLarge: '{{name}} is larger than {{mb}} MB.',
      fileType: '{{name}} is not a PDF or a photo.',
    },
    months: {
      january: 'January',
      february: 'February',
      march: 'March',
      april: 'April',
      may: 'May',
      june: 'June',
      july: 'July',
      august: 'August',
      september: 'September',
      october: 'October',
      november: 'November',
      december: 'December',
      jan: 'Jan',
      feb: 'Feb',
      mar: 'Mar',
      apr: 'Apr',
      mayShort: 'May',
      jun: 'Jun',
      jul: 'Jul',
      aug: 'Aug',
      sep: 'Sep',
      oct: 'Oct',
      nov: 'Nov',
      dec: 'Dec',
      monthYear: '{{month}} {{year}}',
    },
    charts: {
      vsMonth: '{{pct}}% vs {{month}}',
      titleCurrency: '{{title}} · {{currency}}',
      rowMonth: '{{label}} · {{month}}',
    },
    reports: {
      profitAndLoss: 'Profit and loss',
      profitAndLossHint: 'Income and expenses for a period, and the profit they leave.',
      balanceSheet: 'Balance sheet',
      balanceSheetHint: 'What the business owns and owes on a date.',
      cashFlow: 'Cash flow',
      cashFlowHint:
        'Where cash came from and went in a period: operating, investing and financing.',
      thisMonth: 'This month',
      lastMonth: 'Last month',
      thisQuarter: 'This quarter',
      lastQuarter: 'Last quarter',
      thisYearToDate: 'This financial year to date',
      thisYear: 'This financial year',
      lastYear: 'Last financial year',
      endOfThisMonth: 'End of this month',
      endOfLastMonth: 'End of last month',
      endOfThisQuarter: 'End of this quarter',
      endOfLastQuarter: 'End of last quarter',
      today: 'Today',
      endOfThisYear: 'End of this financial year',
      endOfLastYear: 'End of last financial year',
      financialStatements: 'Financial statements',
      taxReturns: '{{tax}} returns',
      otherReports: 'Other reports',
      report: 'Report',
      asOf: 'As of {{date}}',
      periodCurrency: '{{period}} · {{currency}}',
      indirect: 'Indirect method. Amounts in {{currency}}.',
      accrual: 'Accrual basis. Amounts in {{currency}}.',
    },
    tax: {
      gst: 'GST',
      vat: 'VAT',
      dueSoon: 'Due soon',
      payable: 'Payable',
      refundable: 'Refundable',
      gstIntroGstin:
        'GSTIN {{gstin}}. Your accountant prepares and files GSTR-1 and GSTR-3B; this shows where each month stands.',
      gstIntroName:
        '{{name}}. Your accountant prepares and files GSTR-1 and GSTR-3B; this shows where each month stands.',
      gstIntro:
        'Your accountant prepares and files GSTR-1 and GSTR-3B; this shows where each month stands.',
      documents_one: '{{count}} document',
      documents_other: '{{count}} documents',
      payableInCash: 'Payable in cash',
      noGstMonths: 'No GST months yet',
      earlierMonths: 'Earlier months',
      outwardTaxable: 'Outward taxable supplies',
      outputTax: 'Output tax',
      inputTaxCredit: 'Input tax credit',
      returnDue: '{{name}} due {{date}}',
      vatIntro: 'Your accountant prepares and files the VAT 201 return for each period.',
      vatDates: '{{from}} to {{to}} · due {{due}}',
      vatDatesFiled: '{{from}} to {{to}} · due {{due}} · filed {{filed}}',
      outputVat: 'Output VAT',
      inputVat: 'Input VAT',
      noVatReturns: 'No VAT returns yet',
      earlierReturns: 'Earlier returns',
    },
    home: {
      cashPosition: 'Cash position · {{currency}}',
      upload: 'Upload',
      invoices: 'Invoices',
      bills: 'Bills',
      approvals: 'Approvals',
      thisVsLast: 'This month vs last month',
      sales: 'Sales',
      profit: 'Profit',
      needsAttention: 'Needs your attention',
      allCaughtUp: 'You are all caught up',
      accounts: 'Accounts',
      moneyInOut: 'Money in and out',
      owedToYou: 'Owed to you',
      youOwe: 'You owe',
      invoices_one: '{{count}} invoice',
      invoices_other: '{{count}} invoices',
      bills_one: '{{count}} bill',
      bills_other: '{{count}} bills',
      overdue: '{{count}} overdue',
      returnLabel: 'Return {{label}}',
      payableDue: 'Payable · due {{date}}',
      refundableDue: 'Refundable · due {{date}}',
      noReturnDue: 'No {{tax}} return due',
    },
    notifications: {
      recent: 'Recent',
      markAllRead: 'Mark all as read',
      unread: 'Unread: {{title}}',
      none: 'No notifications',
      emailMeAbout: 'Email me about',
      appOnly: 'Notifications appear in the app only for your role.',
      notDelivered: 'This server keeps emails on its Sent emails list and does not deliver them.',
      emailSwitch: 'Email: {{title}}',
      couldNotSave: 'Could not save',
      nothingToSet: 'Nothing to set for your role.',
      approvalRequested: 'Documents awaiting approval',
      approvalRequestedHint: 'A draft invoice or bill is waiting for your approval.',
      approvalDecided: 'Approval decisions',
      approvalDecidedHint: 'A document you sent for approval was approved or rejected.',
      uploadReceived: 'New uploads',
      uploadReceivedHint: 'A bill or receipt was added to the inbox.',
      uploadProcessed: 'Uploads processed',
      uploadProcessedHint: 'A bill or receipt you uploaded was recorded, or could not be.',
      vatReturnDue: 'Tax return due',
      vatReturnDueHint: 'A tax return is due within seven days and has not been filed.',
      messageReceived: 'Messages',
      messageReceivedHint: 'Your accountant replied in Chat.',
    },
    chat: {
      noMessages: 'No messages yet',
      noMessagesHint: 'Ask your accountant about a bill, an invoice or your taxes.',
      earlier: 'Earlier messages',
      accountantName: '{{name}} · Outss',
      placeholder: 'Message your accountant',
      message: 'Message',
      send: 'Send',
    },
    assistant: {
      owed: 'How much do customers owe me?',
      overdue: 'Which invoices are overdue?',
      sales: 'How did sales do this month?',
      vatDue: 'When is my next VAT return due?',
      spend: 'What did I spend most on last month?',
      title: 'Ask about your business',
      intro: 'Answers will come from your own books, with every figure from your accounts.',
      tryAsking: 'Try asking',
      questionSoon: '{{question}} (coming soon)',
      placeholder: 'Ask Outss AI · coming soon',
      inputLabel: 'Question (coming soon)',
    },
    upload: {
      ownerOnly: 'Uploading is for the company owner',
      viewOnly: 'Your role can view documents only.',
      someNotAdded: 'Some files were not added',
      cameraNotAllowed: 'Camera not allowed',
      cameraHint: 'Allow Outss Accounting to use the camera in your phone settings.',
      sentTitle: 'Sent to your accountant',
      sentBody: 'They will record it and you will see the status in Documents.',
      addFiles: 'Add files',
      camera: 'Camera',
      photos: 'Photos',
      files: 'Files',
      limit: 'PDF or photo, up to {{mb}} MB each.',
      sending: 'Sending…',
      sent: 'Sent',
      ready: 'Ready',
      remove: 'Remove {{name}}',
      whatIsIt: 'What is it?',
      note: 'Note for your accountant (optional)',
      notePlaceholder: 'For example: paid in cash, office supplies',
      sendFiles_one: 'Send {{count}} file',
      sendFiles_other: 'Send {{count}} files',
      sendToAccountant: 'Send to your accountant',
    },
    documents: {
      inbox: 'Inbox',
      approvals: 'Approvals',
      couldNotOpen: 'Could not open the file',
      viewerNote:
        'Bills and receipts sent to your accountant. Your role can view them; the owner uploads.',
      nothingWaiting: 'Nothing waiting',
      noDocuments: 'No documents here',
      note: 'Note: {{note}}',
      waiting: 'Waiting',
      history: 'History',
      nothingForApproval: 'Nothing waiting for approval',
      noDecisions: 'No decisions yet',
      waitingHint:
        'When your accountant asks you to approve an invoice or a bill, it appears here.',
      invoiceNumber: 'Invoice {{number}}',
      billNumber: 'Bill {{number}}',
      draft: '(draft)',
      dated: 'Dated {{date}}',
      datedDue: 'Dated {{date}} · due {{due}}',
      askedBy: 'Asked by {{who}} on {{when}}',
      yourAccountant: 'your accountant',
      approve: 'Approve',
      reject: 'Reject',
      couldNotDecide: 'Could not save your decision',
      invoiceRef: 'invoice {{number}}',
      invoiceRefNone: 'invoice',
      billRef: 'bill {{number}}',
      billRefNone: 'bill',
      approveTitle: 'Approve {{what}}?',
      rejectTitle: 'Reject {{what}}?',
      approveBody: 'Your accountant can then post it. A comment is optional.',
      rejectBody: 'Tell your accountant what needs to change. A comment is required.',
      commentOptional: 'Comment (optional)',
      whyReject: 'Why are you rejecting it?',
    },
    newDocument: {
      newInvoice: 'New invoice',
      newBill: 'New bill',
      cannotInvoices: 'You cannot create invoices',
      cannotBills: 'You cannot create bills',
      askSwitchOn: 'Ask your accountant to switch this on for your company.',
      checkFields: 'Check the fields marked in red.',
      draftNoteInvoice:
        'The invoice goes to your accountant as a draft. It is posted to your books once they approve it.',
      draftNoteBill:
        'The bill goes to your accountant as a draft. It is posted to your books once they approve it.',
      customer: 'Customer',
      vendor: 'Vendor',
      chooseCustomer: 'Choose a customer',
      chooseVendor: 'Choose a vendor',
      invoiceDate: 'Invoice date',
      billDate: 'Bill date',
      datePlaceholder: 'dd/mm/yyyy',
      vendorBillNo: "Vendor's bill no.",
      asPrinted: 'As printed on the bill',
      terms: 'Terms',
      line: 'Line {{n}}',
      removeLine: 'Remove line {{n}}',
      description: 'Description',
      whatSold: 'What you sold',
      whatBought: 'What you bought (optional)',
      category: 'Category',
      chooseCategory: 'Choose a category',
      quantity: 'Quantity',
      price: 'Price ({{currency}})',
      hsnSac: 'HSN/SAC',
      hsnPlaceholder: '4, 6 or 8 digits',
      taxCode: '{{tax}} code',
      noTaxCodes: 'No {{tax}} codes are set up for this company.',
      addLine: 'Add a line',
      messageOnInvoice: 'Message on the invoice',
      memo: 'Memo',
      subtotal: 'Subtotal ({{currency}})',
      taxAdded: "{{tax}} is added from each line's code when you save.",
      saveDraft: 'Save draft',
      sendToAccountant: 'Send to accountant',
      noCustomers: 'No customers yet. Ask your accountant to add the customer.',
      noVendors: 'No vendors yet. Ask your accountant to add the vendor.',
      noExpenseAccounts: 'No expense accounts in the chart of accounts.',
      search: 'Search',
      nothingMatches: 'Nothing matches',
      usualTerms: 'Usual terms',
      onReceipt: 'On receipt',
      days_one: '{{count}} day',
      days_other: '{{count}} days',
      errChooseCustomer: 'Choose a customer.',
      errChooseVendor: 'Choose a vendor.',
      errDate: 'Enter the date as dd/mm/yyyy.',
      errReference: 'Keep it under 100 characters.',
      errNoLines: 'Add at least one line.',
      errQuantity: 'Enter a quantity above 0.',
      errPrice: 'Enter the price.',
      errPriceFormat: 'A number with at most 2 decimals, e.g. 120.50.',
      errDescription: 'Say what was sold.',
      errCategory: 'Choose a category.',
      errTaxCode: 'Choose a tax code.',
      errHsn: 'HSN/SAC is 4, 6 or 8 digits.',
    },
    signIn: {
      brand: 'Outss Accounting',
      title: 'Your business, with your accountant',
      signIn: 'Sign in',
      orDemo: 'or explore the demo',
      demoOwner: 'Try the demo as a business owner',
      demoAccountant: 'Try the demo as an accountant',
      server: 'Server',
      serverHint: 'Your accountant tells you this if it is not the usual one.',
      useServer: 'Use this server',
      default: 'Default',
      serverLine: 'Server: {{server}} ·',
      change: 'Change',
      serverError:
        'Enter the address of your Outss Accounting server, for example accounting.example.com.',
    },
    orgPicker: {
      noCompany: 'No company yet',
      noCompanyHint:
        "Your accountant adds you to your company's books. Ask them to invite this email address.",
      pick: 'Pick the company whose books you want to see.',
    },
    lists: {
      invoices: 'Invoices',
      customers: 'Customers',
      bills: 'Bills',
      expenses: 'Expenses',
      vendors: 'Vendors',
      invoiceCount_one: '{{count}} invoice',
      invoiceCount_other: '{{count}} invoices',
      billCount_one: '{{count}} bill',
      billCount_other: '{{count}} bills',
      expenseCount_one: '{{count}} expense',
      expenseCount_other: '{{count}} expenses',
      customerCount_one: '{{count}} customer',
      customerCount_other: '{{count}} customers',
      vendorCount_one: '{{count}} vendor',
      vendorCount_other: '{{count}} vendors',
      noInvoices: 'No invoices',
      noBills: 'No bills',
      noExpenses: 'No expenses',
      noCustomers: 'No customers',
      noVendors: 'No vendors',
      searchCustomers: 'Search customers',
      searchVendors: 'Search vendors',
      search: 'Search',
      noPayee: '(no payee)',
      open: 'Open {{amount}}',
    },
    document: {
      invoice: 'Invoice',
      invoiceNumber: 'Invoice {{number}}',
      bill: 'Bill',
      billNumber: 'Bill {{number}}',
      expense: 'Expense',
      expenseNumber: 'Expense {{number}}',
      customer: 'Customer',
      vendor: 'Vendor',
      payee: 'Payee',
      balanceDue: 'Balance due',
      details: 'Details',
      number: 'Number',
      date: 'Date',
      dueDate: 'Due date',
      placeOfSupply: 'Place of supply',
      paidFrom: 'Paid from',
      reference: 'Reference',
      lines: 'Lines',
      hsnSac: 'HSN/SAC {{code}}',
      opening: 'Brought over from your previous books as an open balance, without lines.',
      summary: 'Summary',
      subtotal: 'Subtotal',
      taxOn: '{{code}} on {{amount}}',
      total: 'Total',
      payments: 'Payments and credits',
      memo: 'Memo',
      payment: 'Payment',
      creditNote: 'Credit note',
      debitNote: 'Debit note',
      journal: 'Journal',
    },
    share: {
      couldNotGetPdf: 'Could not get the PDF',
      noWhatsApp: 'WhatsApp is not installed on this phone.',
      noEmail: 'No email app is set up on this phone.',
      sendAnotherWay: 'Send another way?',
      otherApps: 'Other apps',
      whatsApp: 'WhatsApp',
      email: 'Email',
      pdf: 'PDF',
      more: 'More',
      openPdf: 'Open the PDF',
      moreWays: 'More ways to send',
      sendByWhatsApp: 'Send by WhatsApp',
      sendByEmail: 'Send by email',
      preparing: 'Preparing…',
    },
    api: {
      network: 'Could not reach the server. Check your connection.',
    },
  },
};

export const ar = {
  mobileScreens: {
    kit: {
      tryAgain: 'إعادة المحاولة',
      comingSoon: 'قريباً',
      unreadBadge: '{{label}}، {{count}} غير مقروءة',
    },
    status: {
      void: 'ملغاة',
      draft: 'مسودة',
      applied: 'مطبَّق',
      partlyApplied: 'مطبَّق جزئياً',
      unapplied: 'غير مطبَّق',
      paid: 'مدفوعة',
      overdue: 'متأخرة',
      overduePartlyPaid: 'متأخرة · مدفوعة جزئياً',
      partiallyPaid: 'مدفوعة جزئياً',
      unpaid: 'غير مدفوعة',
      recorded: 'مسجَّل',
      approved: 'معتمدة',
      withdrawn: 'مسحوبة',
      notRecorded: 'غير مسجَّل',
      rejected: 'مرفوضة',
    },
    filter: {
      all: 'الكل',
      unpaid: 'غير مدفوعة',
      overdue: 'متأخرة',
      paid: 'مدفوعة',
      drafts: 'المسودات',
    },
    roles: {
      admin: 'مسؤول',
      accountant: 'محاسب',
      owner: 'مالك',
      viewer: 'مُطّلع',
      rejectWhy: 'اذكر سبب الرفض.',
      commentTooLong: 'يجب ألا يتجاوز التعليق 2,000 حرف.',
    },
    portal: {
      kindBill: 'فاتورة مورد للدفع',
      kindReceipt: 'إيصال',
      kindInvoice: 'فاتورة',
      kindOther: 'أخرى',
      toReview: 'للمراجعة',
      recorded: 'مسجَّلة',
      notRecorded: 'غير مسجَّلة',
      all: 'الكل',
      recordedExpense: 'سُجّل كمصروف لـ {{name}}',
      recordedInvoice: 'سُجّل كفاتورة لـ {{name}}',
      recordedBill: 'سُجّل كفاتورة مورد لـ {{name}}',
      awaitingApproval: 'بانتظار الموافقة',
      approved: 'معتمدة',
      rejected: 'مرفوضة',
      withdrawn: 'مسحوبة',
      readyToPost: 'اعتمدتها، وهي جاهزة للترحيل',
      waitingForYou: 'بانتظارك',
      withAccountant: 'لدى محاسبك',
      fileEmpty: 'الملف {{name}} فارغ.',
      fileTooLarge: 'حجم الملف {{name}} أكبر من {{mb}} ميغابايت.',
      fileType: 'الملف {{name}} ليس ملف PDF ولا صورة.',
    },
    months: {
      january: 'يناير',
      february: 'فبراير',
      march: 'مارس',
      april: 'أبريل',
      may: 'مايو',
      june: 'يونيو',
      july: 'يوليو',
      august: 'أغسطس',
      september: 'سبتمبر',
      october: 'أكتوبر',
      november: 'نوفمبر',
      december: 'ديسمبر',
      jan: 'يناير',
      feb: 'فبراير',
      mar: 'مارس',
      apr: 'أبريل',
      mayShort: 'مايو',
      jun: 'يونيو',
      jul: 'يوليو',
      aug: 'أغسطس',
      sep: 'سبتمبر',
      oct: 'أكتوبر',
      nov: 'نوفمبر',
      dec: 'ديسمبر',
      monthYear: '{{month}} {{year}}',
    },
    charts: {
      vsMonth: '⁦{{pct}}%⁩ مقارنة بـ{{month}}',
      titleCurrency: '{{title}} · {{currency}}',
      rowMonth: '{{label}} · {{month}}',
    },
    reports: {
      profitAndLoss: 'الأرباح والخسائر',
      profitAndLossHint: 'الإيرادات والمصروفات خلال فترة، والربح المتبقي منها.',
      balanceSheet: 'الميزانية العمومية',
      balanceSheetHint: 'ما تملكه الشركة وما عليها في تاريخ معيّن.',
      cashFlow: 'التدفقات النقدية',
      cashFlowHint: 'مصادر النقد واستخداماته خلال فترة: التشغيلية والاستثمارية والتمويلية.',
      thisMonth: 'هذا الشهر',
      lastMonth: 'الشهر الماضي',
      thisQuarter: 'هذا الربع',
      lastQuarter: 'الربع الماضي',
      thisYearToDate: 'السنة المالية حتى اليوم',
      thisYear: 'هذه السنة المالية',
      lastYear: 'السنة المالية الماضية',
      endOfThisMonth: 'نهاية هذا الشهر',
      endOfLastMonth: 'نهاية الشهر الماضي',
      endOfThisQuarter: 'نهاية هذا الربع',
      endOfLastQuarter: 'نهاية الربع الماضي',
      today: 'اليوم',
      endOfThisYear: 'نهاية هذه السنة المالية',
      endOfLastYear: 'نهاية السنة المالية الماضية',
      financialStatements: 'القوائم المالية',
      taxReturns: 'إقرارات {{tax}}',
      otherReports: 'تقارير أخرى',
      report: 'تقرير',
      asOf: 'كما في {{date}}',
      periodCurrency: '{{period}} · {{currency}}',
      indirect: 'الطريقة غير المباشرة. المبالغ بعملة {{currency}}.',
      accrual: 'أساس الاستحقاق. المبالغ بعملة {{currency}}.',
    },
    tax: {
      gst: 'ضريبة السلع والخدمات',
      vat: 'ضريبة القيمة المضافة',
      dueSoon: 'يستحق قريباً',
      payable: 'مستحق الدفع',
      refundable: 'قابل للاسترداد',
      gstIntroGstin:
        'رقم GSTIN ‏{{gstin}}. يُعدّ محاسبك إقراري GSTR-1 وGSTR-3B ويقدّمهما؛ وتعرض هذه الصفحة وضع كل شهر.',
      gstIntroName:
        '{{name}}. يُعدّ محاسبك إقراري GSTR-1 وGSTR-3B ويقدّمهما؛ وتعرض هذه الصفحة وضع كل شهر.',
      gstIntro: 'يُعدّ محاسبك إقراري GSTR-1 وGSTR-3B ويقدّمهما؛ وتعرض هذه الصفحة وضع كل شهر.',
      documents_zero: 'لا توجد مستندات',
      documents_one: 'مستند واحد',
      documents_two: 'مستندان',
      documents_few: '{{count}} مستندات',
      documents_many: '{{count}} مستنداً',
      documents_other: '{{count}} مستند',
      payableInCash: 'المستحق نقداً',
      noGstMonths: 'لا توجد أشهر لضريبة السلع والخدمات بعد',
      earlierMonths: 'الأشهر السابقة',
      outwardTaxable: 'التوريدات الخاضعة للضريبة الصادرة',
      outputTax: 'ضريبة المخرجات',
      inputTaxCredit: 'رصيد ضريبة المدخلات',
      returnDue: '{{name}} يستحق في {{date}}',
      vatIntro: 'يُعدّ محاسبك إقرار ضريبة القيمة المضافة VAT 201 لكل فترة ويقدّمه.',
      vatDates: 'من {{from}} إلى {{to}} · يستحق في {{due}}',
      vatDatesFiled: 'من {{from}} إلى {{to}} · يستحق في {{due}} · قُدّم في {{filed}}',
      outputVat: 'ضريبة المخرجات',
      inputVat: 'ضريبة المدخلات',
      noVatReturns: 'لا توجد إقرارات ضريبة القيمة المضافة بعد',
      earlierReturns: 'الإقرارات السابقة',
    },
    home: {
      cashPosition: 'الوضع النقدي · {{currency}}',
      upload: 'رفع',
      invoices: 'الفواتير',
      bills: 'فواتير الموردين',
      approvals: 'الموافقات',
      thisVsLast: 'هذا الشهر مقارنة بالشهر الماضي',
      sales: 'المبيعات',
      profit: 'الربح',
      needsAttention: 'بحاجة إلى انتباهك',
      allCaughtUp: 'لا يوجد ما يحتاج إلى انتباهك',
      accounts: 'الحسابات',
      moneyInOut: 'الأموال الواردة والصادرة',
      owedToYou: 'مستحق لك',
      youOwe: 'مستحق عليك',
      invoices_zero: 'لا توجد فواتير',
      invoices_one: 'فاتورة واحدة',
      invoices_two: 'فاتورتان',
      invoices_few: '{{count}} فواتير',
      invoices_many: '{{count}} فاتورة',
      invoices_other: '{{count}} فاتورة',
      bills_zero: 'لا توجد فواتير موردين',
      bills_one: 'فاتورة مورد واحدة',
      bills_two: 'فاتورتا مورد',
      bills_few: '{{count}} فواتير موردين',
      bills_many: '{{count}} فاتورة مورد',
      bills_other: '{{count}} فاتورة مورد',
      overdue: '{{count}} متأخرة',
      returnLabel: 'إقرار {{label}}',
      payableDue: 'مستحق الدفع · يستحق في {{date}}',
      refundableDue: 'قابل للاسترداد · يستحق في {{date}}',
      noReturnDue: 'لا يوجد إقرار {{tax}} مستحق',
    },
    notifications: {
      recent: 'الأحدث',
      markAllRead: 'تحديد الكل كمقروء',
      unread: 'غير مقروء: {{title}}',
      none: 'لا توجد إشعارات',
      emailMeAbout: 'راسلني بالبريد الإلكتروني عن',
      appOnly: 'تظهر الإشعارات في التطبيق فقط لدورك.',
      notDelivered: 'يحتفظ هذا الخادم بالرسائل في قائمة الرسائل المرسلة ولا يسلّمها.',
      emailSwitch: 'البريد الإلكتروني: {{title}}',
      couldNotSave: 'تعذّر الحفظ',
      nothingToSet: 'لا توجد إعدادات لدورك.',
      approvalRequested: 'مستندات بانتظار الموافقة',
      approvalRequestedHint: 'مسودة فاتورة أو فاتورة مورد بانتظار موافقتك.',
      approvalDecided: 'قرارات الموافقة',
      approvalDecidedHint: 'اعتُمد مستند أرسلته للموافقة أو رُفض.',
      uploadReceived: 'ملفات مرفوعة جديدة',
      uploadReceivedHint: 'أُضيفت فاتورة مورد أو إيصال إلى صندوق المستندات.',
      uploadProcessed: 'ملفات مرفوعة تمت معالجتها',
      uploadProcessedHint: 'سُجّلت فاتورة مورد أو إيصال رفعته، أو تعذّر تسجيله.',
      vatReturnDue: 'إقرار ضريبي مستحق',
      vatReturnDueHint: 'يستحق إقرار ضريبي خلال سبعة أيام ولم يُقدَّم بعد.',
      messageReceived: 'الرسائل',
      messageReceivedHint: 'ردّ محاسبك في المحادثة.',
    },
    chat: {
      noMessages: 'لا توجد رسائل بعد',
      noMessagesHint: 'اسأل محاسبك عن فاتورة مورد أو فاتورة أو ضرائبك.',
      earlier: 'الرسائل السابقة',
      accountantName: '{{name}} · Outss',
      placeholder: 'اكتب رسالة لمحاسبك',
      message: 'الرسالة',
      send: 'إرسال',
    },
    assistant: {
      owed: 'كم يدين لي العملاء؟',
      overdue: 'ما الفواتير المتأخرة؟',
      sales: 'كيف كانت المبيعات هذا الشهر؟',
      vatDue: 'متى يستحق إقرار ضريبة القيمة المضافة القادم؟',
      spend: 'على ماذا أنفقت أكثر في الشهر الماضي؟',
      title: 'اسأل عن أعمالك',
      intro: 'تأتي الإجابات من دفاترك، وكل رقم فيها من حساباتك.',
      tryAsking: 'جرّب أن تسأل',
      questionSoon: '{{question}} (قريباً)',
      placeholder: 'اسأل Outss الذكي · قريباً',
      inputLabel: 'السؤال (قريباً)',
    },
    upload: {
      ownerOnly: 'الرفع متاح لمالك الشركة',
      viewOnly: 'يتيح لك دورك عرض المستندات فقط.',
      someNotAdded: 'لم تُضف بعض الملفات',
      cameraNotAllowed: 'الكاميرا غير مسموح بها',
      cameraHint: 'اسمح لتطبيق Outss Accounting باستخدام الكاميرا من إعدادات هاتفك.',
      sentTitle: 'أُرسل إلى محاسبك',
      sentBody: 'سيسجّله محاسبك، وسترى حالته في المستندات.',
      addFiles: 'إضافة ملفات',
      camera: 'الكاميرا',
      photos: 'الصور',
      files: 'الملفات',
      limit: 'ملف PDF أو صورة، حتى {{mb}} ميغابايت لكل ملف.',
      sending: 'جارٍ الإرسال…',
      sent: 'أُرسل',
      ready: 'جاهز',
      remove: 'إزالة {{name}}',
      whatIsIt: 'ما نوعه؟',
      note: 'ملاحظة لمحاسبك (اختياري)',
      notePlaceholder: 'مثلاً: دُفع نقداً، لوازم مكتبية',
      sendFiles_zero: 'إرسال',
      sendFiles_one: 'إرسال ملف واحد',
      sendFiles_two: 'إرسال ملفين',
      sendFiles_few: 'إرسال {{count}} ملفات',
      sendFiles_many: 'إرسال {{count}} ملفاً',
      sendFiles_other: 'إرسال {{count}} ملف',
      sendToAccountant: 'إرسال إلى محاسبك',
    },
    documents: {
      inbox: 'الوارد',
      approvals: 'الموافقات',
      couldNotOpen: 'تعذّر فتح الملف',
      viewerNote:
        'فواتير الموردين والإيصالات المرسلة إلى محاسبك. يتيح لك دورك عرضها؛ والمالك هو من يرفعها.',
      nothingWaiting: 'لا يوجد ما ينتظر',
      noDocuments: 'لا توجد مستندات هنا',
      note: 'ملاحظة: {{note}}',
      waiting: 'بانتظار القرار',
      history: 'السجل',
      nothingForApproval: 'لا يوجد ما ينتظر الموافقة',
      noDecisions: 'لا توجد قرارات بعد',
      waitingHint: 'عندما يطلب منك محاسبك اعتماد فاتورة أو فاتورة مورد، تظهر هنا.',
      invoiceNumber: 'فاتورة {{number}}',
      billNumber: 'فاتورة مورد {{number}}',
      draft: '(مسودة)',
      dated: 'بتاريخ {{date}}',
      datedDue: 'بتاريخ {{date}} · تستحق في {{due}}',
      askedBy: 'طلبها {{who}} في {{when}}',
      yourAccountant: 'محاسبك',
      approve: 'اعتماد',
      reject: 'رفض',
      couldNotDecide: 'تعذّر حفظ قرارك',
      invoiceRef: 'الفاتورة {{number}}',
      invoiceRefNone: 'الفاتورة',
      billRef: 'فاتورة المورد {{number}}',
      billRefNone: 'فاتورة المورد',
      approveTitle: 'اعتماد {{what}}؟',
      rejectTitle: 'رفض {{what}}؟',
      approveBody: 'يمكن لمحاسبك بعدها ترحيلها. التعليق اختياري.',
      rejectBody: 'أخبر محاسبك بما يجب تغييره. التعليق مطلوب.',
      commentOptional: 'تعليق (اختياري)',
      whyReject: 'لماذا ترفضها؟',
    },
    newDocument: {
      newInvoice: 'فاتورة جديدة',
      newBill: 'فاتورة مورد جديدة',
      cannotInvoices: 'لا يمكنك إنشاء فواتير',
      cannotBills: 'لا يمكنك إنشاء فواتير موردين',
      askSwitchOn: 'اطلب من محاسبك تفعيل ذلك لشركتك.',
      checkFields: 'راجع الحقول المحددة باللون الأحمر.',
      draftNoteInvoice: 'تُرسل الفاتورة إلى محاسبك كمسودة، وتُرحَّل إلى دفاترك بعد اعتماده لها.',
      draftNoteBill: 'تُرسل فاتورة المورد إلى محاسبك كمسودة، وتُرحَّل إلى دفاترك بعد اعتماده لها.',
      customer: 'العميل',
      vendor: 'المورد',
      chooseCustomer: 'اختر عميلاً',
      chooseVendor: 'اختر مورداً',
      invoiceDate: 'تاريخ الفاتورة',
      billDate: 'تاريخ فاتورة المورد',
      datePlaceholder: 'dd/mm/yyyy',
      vendorBillNo: 'رقم فاتورة المورد',
      asPrinted: 'كما هو مطبوع على الفاتورة',
      terms: 'شروط الدفع',
      line: 'البند {{n}}',
      removeLine: 'إزالة البند {{n}}',
      description: 'الوصف',
      whatSold: 'ما الذي بعته',
      whatBought: 'ما الذي اشتريته (اختياري)',
      category: 'الفئة',
      chooseCategory: 'اختر فئة',
      quantity: 'الكمية',
      price: 'السعر ({{currency}})',
      hsnSac: 'HSN/SAC',
      hsnPlaceholder: '4 أو 6 أو 8 أرقام',
      taxCode: 'رمز {{tax}}',
      noTaxCodes: 'لم تُعدّ رموز {{tax}} لهذه الشركة.',
      addLine: 'إضافة بند',
      messageOnInvoice: 'رسالة على الفاتورة',
      memo: 'مذكرة',
      subtotal: 'المجموع الفرعي ({{currency}})',
      taxAdded: 'تُضاف {{tax}} عند الحفظ حسب رمز كل بند.',
      saveDraft: 'حفظ كمسودة',
      sendToAccountant: 'إرسال إلى المحاسب',
      noCustomers: 'لا يوجد عملاء بعد. اطلب من محاسبك إضافة العميل.',
      noVendors: 'لا يوجد موردون بعد. اطلب من محاسبك إضافة المورد.',
      noExpenseAccounts: 'لا توجد حسابات مصروفات في دليل الحسابات.',
      search: 'بحث',
      nothingMatches: 'لا توجد نتائج مطابقة',
      usualTerms: 'الشروط المعتادة',
      onReceipt: 'عند الاستلام',
      days_zero: '0 يوم',
      days_one: 'يوم واحد',
      days_two: 'يومان',
      days_few: '{{count}} أيام',
      days_many: '{{count}} يوماً',
      days_other: '{{count}} يوم',
      errChooseCustomer: 'اختر عميلاً.',
      errChooseVendor: 'اختر مورداً.',
      errDate: 'أدخل التاريخ بالصيغة dd/mm/yyyy.',
      errReference: 'يجب ألا يتجاوز 100 حرف.',
      errNoLines: 'أضف بنداً واحداً على الأقل.',
      errQuantity: 'أدخل كمية أكبر من 0.',
      errPrice: 'أدخل السعر.',
      errPriceFormat: 'رقم بخانتين عشريتين كحد أقصى، مثلاً 120.50.',
      errDescription: 'اذكر ما الذي بِيع.',
      errCategory: 'اختر فئة.',
      errTaxCode: 'اختر رمزاً ضريبياً.',
      errHsn: 'يتكون رمز HSN/SAC من 4 أو 6 أو 8 أرقام.',
    },
    signIn: {
      brand: 'Outss Accounting',
      title: 'أعمالك، مع محاسبك',
      signIn: 'تسجيل الدخول',
      orDemo: 'أو استكشف النسخة التجريبية',
      demoOwner: 'جرّب النسخة التجريبية بصفتك صاحب عمل',
      demoAccountant: 'جرّب النسخة التجريبية بصفتك محاسباً',
      server: 'الخادم',
      serverHint: 'يخبرك محاسبك بذلك إن لم يكن الخادم المعتاد.',
      useServer: 'استخدام هذا الخادم',
      default: 'الافتراضي',
      serverLine: 'الخادم: {{server}} ·',
      change: 'تغيير',
      serverError: 'أدخل عنوان خادم Outss Accounting الخاص بك، مثلاً accounting.example.com.',
    },
    orgPicker: {
      noCompany: 'لا توجد شركة بعد',
      noCompanyHint: 'يضيفك محاسبك إلى دفاتر شركتك. اطلب منه دعوة عنوان البريد الإلكتروني هذا.',
      pick: 'اختر الشركة التي تريد الاطلاع على دفاترها.',
    },
    lists: {
      invoices: 'الفواتير',
      customers: 'العملاء',
      bills: 'فواتير الموردين',
      expenses: 'المصروفات',
      vendors: 'الموردون',
      invoiceCount_zero: 'لا توجد فواتير',
      invoiceCount_one: 'فاتورة واحدة',
      invoiceCount_two: 'فاتورتان',
      invoiceCount_few: '{{count}} فواتير',
      invoiceCount_many: '{{count}} فاتورة',
      invoiceCount_other: '{{count}} فاتورة',
      billCount_zero: 'لا توجد فواتير موردين',
      billCount_one: 'فاتورة مورد واحدة',
      billCount_two: 'فاتورتا مورد',
      billCount_few: '{{count}} فواتير موردين',
      billCount_many: '{{count}} فاتورة مورد',
      billCount_other: '{{count}} فاتورة مورد',
      expenseCount_zero: 'لا توجد مصروفات',
      expenseCount_one: 'مصروف واحد',
      expenseCount_two: 'مصروفان',
      expenseCount_few: '{{count}} مصروفات',
      expenseCount_many: '{{count}} مصروفاً',
      expenseCount_other: '{{count}} مصروف',
      customerCount_zero: 'لا يوجد عملاء',
      customerCount_one: 'عميل واحد',
      customerCount_two: 'عميلان',
      customerCount_few: '{{count}} عملاء',
      customerCount_many: '{{count}} عميلاً',
      customerCount_other: '{{count}} عميل',
      vendorCount_zero: 'لا يوجد موردون',
      vendorCount_one: 'مورد واحد',
      vendorCount_two: 'موردان',
      vendorCount_few: '{{count}} موردين',
      vendorCount_many: '{{count}} مورداً',
      vendorCount_other: '{{count}} مورد',
      noInvoices: 'لا توجد فواتير',
      noBills: 'لا توجد فواتير موردين',
      noExpenses: 'لا توجد مصروفات',
      noCustomers: 'لا يوجد عملاء',
      noVendors: 'لا يوجد موردون',
      searchCustomers: 'البحث في العملاء',
      searchVendors: 'البحث في الموردين',
      search: 'بحث',
      noPayee: '(بدون مستفيد)',
      open: 'المتبقي {{amount}}',
    },
    document: {
      invoice: 'فاتورة',
      invoiceNumber: 'فاتورة {{number}}',
      bill: 'فاتورة مورد',
      billNumber: 'فاتورة مورد {{number}}',
      expense: 'مصروف',
      expenseNumber: 'مصروف {{number}}',
      customer: 'العميل',
      vendor: 'المورد',
      payee: 'المستفيد',
      balanceDue: 'الرصيد المستحق',
      details: 'التفاصيل',
      number: 'الرقم',
      date: 'التاريخ',
      dueDate: 'تاريخ الاستحقاق',
      placeOfSupply: 'مكان التوريد',
      paidFrom: 'دُفع من',
      reference: 'المرجع',
      lines: 'البنود',
      hsnSac: 'HSN/SAC ‏{{code}}',
      opening: 'منقول من دفاترك السابقة كرصيد مفتوح، بدون بنود.',
      summary: 'الملخص',
      subtotal: 'المجموع الفرعي',
      taxOn: '{{code}} على {{amount}}',
      total: 'الإجمالي',
      payments: 'الدفعات والأرصدة الدائنة',
      memo: 'مذكرة',
      payment: 'دفعة',
      creditNote: 'إشعار دائن',
      debitNote: 'إشعار مدين',
      journal: 'قيد يومية',
    },
    share: {
      couldNotGetPdf: 'تعذّر الحصول على ملف PDF',
      noWhatsApp: 'تطبيق واتساب غير مثبّت على هذا الهاتف.',
      noEmail: 'لا يوجد تطبيق بريد إلكتروني مُعدّ على هذا الهاتف.',
      sendAnotherWay: 'الإرسال بطريقة أخرى؟',
      otherApps: 'تطبيقات أخرى',
      whatsApp: 'واتساب',
      email: 'البريد',
      pdf: 'PDF',
      more: 'المزيد',
      openPdf: 'فتح ملف PDF',
      moreWays: 'طرق أخرى للإرسال',
      sendByWhatsApp: 'إرسال عبر واتساب',
      sendByEmail: 'إرسال بالبريد الإلكتروني',
      preparing: 'جارٍ التحضير…',
    },
    api: {
      network: 'تعذّر الوصول إلى الخادم. تحقّق من اتصالك.',
    },
  },
};
